import { useCallback, useEffect, useRef, useState } from 'react';

import { CircleAlertIcon, InfoIcon } from 'lucide-react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { TypingIndicator } from '@/components/atoms/TypingIndicator/TypingIndicator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAutoBrain } from '@/hooks/useAutoBrain';
import useExpressions from '@/hooks/useExpressions';
import useIntentRecommendation from '@/hooks/useIntentRecommendation';
import useIntents from '@/hooks/useIntents';
import { cn } from '@/lib/utils';
import { updatePhrases } from '@/redux/auto-ai/actions';
import {
  updateExpression,
  deleteExpression,
  createExpression,
  appendExpressions,
} from '@/redux/expressions/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { TYPE } from '@/shared/types/recommendations';
import { RootState } from '@/shared/types/state';
import { isKeyEnter } from '@/util/util';

import RecommendationsTable from '../RecommendationsTable/RecommendationsTable';
import TrainingPhrase from '../TrainingPhrase/TrainingPhrase';

import styles from './TrainingPhrases.module.scss';

interface Props {
  isLoading: boolean;
  readOnly?: boolean;
}

/**
 * TrainingPhrases component for managing training phrases/expressions for intents
 *
 * This component provides a comprehensive interface for creating, editing, and managing
 * training phrases for AI intents. It supports:
 * - Adding new phrases via text input (single or multiple lines)
 * - Real-time editing of existing phrases
 * - Duplicate detection and validation
 * - Auto-generated phrase recommendations
 * - Intent recommendation integration
 *
 * @param isLoading - Whether the component is in a loading state
 * @param readOnly - Whether the component is in read-only mode
 * @returns JSX element
 */
const TrainingPhrases = ({ isLoading, readOnly = false }: Props) => {
  // ============================================================================
  // HOOKS & EXTERNAL DATA
  // ============================================================================

  const { t } = useTranslation();
  const { intentName } = useParams();
  const brainId = useSelector(selectBrainId);
  const dispatch = useDispatch();

  // Reference to the new phrase input field for focus management
  const newPhraseInputRef = useRef<HTMLTextAreaElement>(null);

  // Intent recommendation system integration
  const { recommendedIntent, updateRecommendedExpression, updateRecommendedExpressions } =
    useIntentRecommendation(brainId, intentName);

  // Auto-generated expressions from AI brain
  const autoGeneratedExpressions = useSelector((state: RootState) => state.autoAI?.phrases);
  const {
    getAutoExpressions,
    isLoading: isAutoGenerating,
    error: autoGenerationError,
  } = useAutoBrain();

  // Current expressions data and state
  const { expressions, expressionsWithId, isDraft } = useExpressions(brainId, intentName);
  const { intent, filteredExpressions } = useIntents(brainId, intentName);

  // ============================================================================
  // LOCAL STATE
  // ============================================================================

  // New phrase input state
  const [newPhraseText, setNewPhraseText] = useState('');
  const [hasNewPhraseError, setHasNewPhraseError] = useState(false);
  /** Capped to one row at rest; the cap is lifted on click so pasted text stays readable. */
  const [newPhraseMaxRows, setNewPhraseMaxRows] = useState<number | undefined>(1);
  const [duplicateCount, setDuplicateCount] = useState<number | null>(null);

  // Inline editing state for existing phrases
  const [editingPhrases, setEditingPhrases] = useState<Record<string, string>>({});
  const [phraseValidationErrors, setPhraseValidationErrors] = useState<Record<string, boolean>>({});

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Determine UI state based on data availability
  const hasExistingRecommendedIntent = recommendedIntent && !!intent;
  const shouldShowRecommendations = hasExistingRecommendedIntent && filteredExpressions.length > 0;
  const shouldShowEmptyStateBanner = expressions?.length === 0;

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles the creation of a new expression and triggers auto-brain if needed
   *
   * This callback is triggered when a new expression is successfully created.
   * It conditionally triggers auto-brain generation for additional suggestions.
   *
   * @param newExpressionText - The text of the newly created expression
   */
  const handleExpressionCreated = useCallback(
    (newExpressionText: string) => {
      // Only trigger auto-brain if we're not showing recommendations and in draft mode
      if (!shouldShowRecommendations && isDraft) {
        getAutoExpressions(newExpressionText);
      }
    },
    [getAutoExpressions, isDraft, shouldShowRecommendations]
  );

  /**
   * Handles adding a new training phrase(s) - supports both single and multiple phrases from paste
   *
   * This function processes the new phrase input, validates it, and creates expressions.
   * It supports multi-line input for bulk phrase creation.
   */
  const handleCreateNewPhrase = useCallback(() => {
    const trimmedText = newPhraseText.trim();

    // If input is empty, just clear any errors and return (don't show error for empty input)
    if (!trimmedText) {
      setHasNewPhraseError(false);
      setDuplicateCount(null);
      return;
    }

    // Split input into individual lines for multi-line support
    const phraseLines = trimmedText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    // Validate that we have at least one valid line after trimming
    if (phraseLines.length === 0) {
      setHasNewPhraseError(false);
      setDuplicateCount(null);
      return;
    }

    // Check if this is a single phrase or multiple phrases
    const isMultiplePhrases = phraseLines.length > 1;

    if (isMultiplePhrases) {
      // MULTIPLE PHRASES: Use Set operations for efficient duplicate handling
      // Create a Set from new input phrases (removes internal duplicates)
      const newPhrasesSet = new Set(phraseLines.map((line) => line.toLowerCase()));
      // Create a Set from existing phrases
      const existingPhrasesSet = new Set(expressions?.map((expr) => expr.toLowerCase()) || []);
      // Find new phrases
      const phrasesToAdd = [];
      for (const phrase of newPhrasesSet) {
        if (!existingPhrasesSet.has(phrase)) {
          phrasesToAdd.push(phrase);
        }
      }
      // Calculate how many new phrases were duplicates with existing ones
      const duplicates = newPhrasesSet.size - phrasesToAdd.length;

      if (phrasesToAdd.length === 0) {
        // All phrases were duplicates with existing expressions
        setHasNewPhraseError(true);
        setDuplicateCount(null);
        return;
      }

      // Add unique phrases
      phrasesToAdd.forEach((phrase) => {
        dispatch(createExpression({ newText: phrase }));
        handleExpressionCreated(phrase);
      });

      // Show feedback about duplicates
      if (duplicates > 0) {
        setDuplicateCount(duplicates);
      } else {
        setDuplicateCount(null);
      }
    } else {
      // SINGLE PHRASE: Keep strict validation
      const isDuplicate = expressions?.some(
        (expr) => expr.toLowerCase() === phraseLines[0].toLowerCase()
      );

      if (isDuplicate) {
        setHasNewPhraseError(true);
        setDuplicateCount(null);
        return;
      }

      dispatch(createExpression({ newText: phraseLines[0] }));
      handleExpressionCreated(phraseLines[0]);
      setDuplicateCount(null);
    }

    setHasNewPhraseError(false);
    setNewPhraseText('');
  }, [newPhraseText, expressions, dispatch, handleExpressionCreated]);

  /**
   * Handles editing individual training phrases
   *
   * This function manages the inline editing state and validates input in real-time.
   * It checks for duplicates and updates validation errors accordingly.
   *
   * @param phraseId - The ID of the phrase being edited
   * @param newValue - The new value of the phrase
   */
  const handleEditPhrase = useCallback(
    (phraseId: string, newValue: string) => {
      // Update the editing state with the new value
      setEditingPhrases((prev) => ({ ...prev, [phraseId]: newValue }));

      const trimmedValue = newValue.trim();
      const firstLine = trimmedValue.split('\n')[0];

      if (firstLine) {
        // Check if the new value duplicates an existing phrase (excluding current phrase)
        const isDuplicate = expressionsWithId.some(
          (expr) => expr.id !== phraseId && expr.text.toLowerCase() === firstLine.toLowerCase()
        );

        if (isDuplicate) {
          // Mark this phrase as having a validation error
          setPhraseValidationErrors((prev) => ({ ...prev, [phraseId]: true }));
        } else {
          // Clear validation error if it exists
          setPhraseValidationErrors((prev) => {
            if (!prev[phraseId]) {
              return prev;
            }
            const updated = { ...prev };
            delete updated[phraseId];
            return updated;
          });
        }
      } else {
        // Clear validation error for empty input
        setPhraseValidationErrors((prev) => {
          if (!prev[phraseId]) {
            return prev;
          }
          const updated = { ...prev };
          delete updated[phraseId];
          return updated;
        });
      }
    },
    [expressionsWithId]
  );

  /**
   * Handles the blur event for individual training phrases (saves changes)
   *
   * This function processes the final edited value and either saves it, deletes it
   * (if empty), or shows validation errors for duplicates.
   *
   * @param phraseId - The ID of the phrase being edited
   */
  const handleSavePhrase = useCallback(
    (phraseId: string) => {
      const currentInput = editingPhrases[phraseId];
      const originalText = expressionsWithId.find((expr) => expr.id === phraseId)?.text ?? '';

      // If no input exists, skip processing
      if (currentInput === undefined) {
        return;
      }

      const trimmedInput = currentInput.trim();
      const firstLine = trimmedInput.split('\n')[0];

      // If the phrase is empty, delete it
      if (!firstLine) {
        dispatch(deleteExpression({ id: phraseId }));

        // Clean up state
        setPhraseValidationErrors((prev) => {
          const updated = { ...prev };
          delete updated[phraseId];
          return updated;
        });
        setEditingPhrases((prev) => {
          const updated = { ...prev };
          delete updated[phraseId];
          return updated;
        });
        return;
      }

      // Check for duplicates before saving
      const isDuplicate = expressionsWithId.some(
        (expr) => expr.id !== phraseId && expr.text.toLowerCase() === firstLine.toLowerCase()
      );

      // If there is a duplicate, set the error state and return
      if (isDuplicate) {
        setPhraseValidationErrors((prev) => ({ ...prev, [phraseId]: true }));
        return;
      }

      // Clear validation errors
      setPhraseValidationErrors((prev) => {
        if (!prev[phraseId]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[phraseId];
        return updated;
      });

      // Only update if the text has actually changed
      if (firstLine === originalText) {
        return;
      }

      // Save the updated phrase
      dispatch(updateExpression({ id: phraseId, newText: firstLine }));
      setEditingPhrases((prev) => ({ ...prev, [phraseId]: firstLine }));
    },
    [dispatch, editingPhrases, expressionsWithId]
  );

  /**
   * Handles deletion of individual training phrases
   *
   * This function removes a phrase from the system and cleans up related state.
   *
   * @param phraseId - The ID of the phrase to delete
   */
  const handleDeletePhrase = useCallback(
    (phraseId: string) => {
      // Remove the phrase from the backend
      dispatch(deleteExpression({ id: phraseId }));

      // Clean up local state
      setPhraseValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[phraseId];
        return updated;
      });
      setEditingPhrases((prev) => {
        const updated = { ...prev };
        delete updated[phraseId];
        return updated;
      });
    },
    [dispatch]
  );

  // ============================================================================
  // HANDLER FACTORIES
  // ============================================================================

  // These factory functions create specific handlers for each phrase item
  // They use closure to capture the phraseId and return event handlers

  /**
   * Creates an onChange handler for editing a specific phrase
   *
   * @param phraseId - The ID of the phrase
   * @returns onChange handler
   */
  const createEditHandler = (phraseId: string) => (value: string) =>
    handleEditPhrase(phraseId, value);

  /**
   * Creates an onBlur handler for saving a specific phrase
   *
   * @param phraseId - The ID of the phrase
   * @returns onBlur handler
   */
  const createSaveHandler = (phraseId: string) => () => handleSavePhrase(phraseId);

  /**
   * Creates an onDelete handler for deleting a specific phrase
   *
   * @param phraseId - The ID of the phrase
   * @returns onDelete handler
   */
  const createDeleteHandler = (phraseId: string) => () => handleDeletePhrase(phraseId);

  // ============================================================================
  // SIDE EFFECTS
  // ============================================================================

  // Reset all local state when the intent changes
  useEffect(() => {
    setNewPhraseText('');
    setHasNewPhraseError(false);
    setDuplicateCount(null);
    setEditingPhrases({});
    setPhraseValidationErrors({});
  }, [intentName]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      {/* New Phrase Input Section */}
      <div className={styles.inputWrapper}>
        <Textarea
          ref={newPhraseInputRef}
          placeholder={t('training_phrases.placeholder')}
          value={newPhraseText}
          onChange={(e) => {
            setNewPhraseText(e.target.value);
            if (hasNewPhraseError) {
              setHasNewPhraseError(false);
            }
          }}
          onClick={() => setNewPhraseMaxRows(undefined)}
          onMouseDown={() => setNewPhraseMaxRows(undefined)}
          onBlur={() => {
            setNewPhraseMaxRows(1);
            handleCreateNewPhrase();
          }}
          onKeyDown={(e) => {
            if (isKeyEnter(e)) {
              e.preventDefault();
              handleCreateNewPhrase();
            }
          }}
          data-testid="new-expression-input"
          autoSize
          maxRows={newPhraseMaxRows}
          className={cn(
            'w-full resize-none overflow-hidden rounded-md border bg-background px-4 py-2 text-sm leading-5 text-foreground outline-none placeholder:text-foreground-muted',
            hasNewPhraseError ? 'border-stroke-destructive' : 'border-stroke'
          )}
        />

        {/* Error message for invalid input */}
        {hasNewPhraseError && (
          <div className={styles.error_message}>
            <span className="text-sm leading-5 font-normal text-warning">
              {t('training_phrases.error')}
            </span>
          </div>
        )}

        {/* Feedback message for discarded duplicates */}
        {duplicateCount > 0 && (
          <div className={styles.error_message}>
            <span className="text-sm leading-5 font-normal text-warning">
              {t('training_phrases.duplicates_discarded', { 0: duplicateCount })}
            </span>
          </div>
        )}
      </div>

      {/* Scrollable Content Area */}
      <Scrollbars>
        <div className={styles.wrapper}>
          {/* Intent Recommendations Section */}
          {shouldShowRecommendations && (
            <RecommendationsTable
              items={filteredExpressions}
              type={TYPE.LOGS}
              onSaveClick={(text: string) => {
                updateRecommendedExpression('accept', text);
                // Focus input after accepting recommendation
                setTimeout(() => {
                  newPhraseInputRef.current?.focus();
                }, 0);
              }}
              onRejectClick={(text: string) => updateRecommendedExpression('reject', text)}
              onSaveAllClick={() => updateRecommendedExpressions('accept', filteredExpressions)}
              onRejectAllClick={() => updateRecommendedExpressions('reject', filteredExpressions)}
            />
          )}

          {/* Auto-Generation Loading/Error States */}
          {(!autoGeneratedExpressions || autoGeneratedExpressions?.length === 0) && (
            <div className={styles.loading}>
              {/* Show typing indicator while generating */}
              {isAutoGenerating && <TypingIndicator className="text-ai" />}

              {/* Show error message if generation fails */}
              {autoGenerationError && (
                <div className={styles.autoError}>
                  <CircleAlertIcon size={16} className="text-destructive" />
                  <span className="text-sm leading-5 font-normal">
                    {t('auto_brain.expressions_error')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Auto-Generated Recommendations Section */}
          {autoGeneratedExpressions?.length > 0 && !shouldShowRecommendations && (
            <RecommendationsTable
              items={autoGeneratedExpressions}
              type={TYPE.AUTO}
              isLoadingNew={isAutoGenerating}
              onSaveClick={(text: string) => {
                // Accept auto-generated expression
                dispatch(updatePhrases({ text }));
                dispatch(appendExpressions({ newExpressions: [text] }));

                // Focus and select input for easy editing
                setTimeout(() => {
                  newPhraseInputRef.current?.focus();
                  newPhraseInputRef.current?.select();
                }, 0);
              }}
              onRejectClick={(text: string) => {
                // Reject auto-generated expression
                dispatch(updatePhrases({ text }));
              }}
            />
          )}

          {/* Phrases Count Header */}
          <div className={styles.subtitle}>
            <span className="text-sm leading-5 font-medium">{t('training_phrases.subtitle')}</span>
            <span className="text-sm leading-5 font-medium">{expressions?.length}</span>
          </div>

          {/* Phrases List Section */}
          {isLoading ? (
            // Show skeleton loading state
            Array(5)
              .fill('')
              // eslint-disable-next-line react/no-array-index-key
              .map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
          ) : (
            // Render actual phrases list
            <>
              {expressionsWithId?.map(({ id, text }) => (
                <TrainingPhrase
                  key={id}
                  value={editingPhrases[id] ?? text}
                  onChange={createEditHandler(id)}
                  onBlur={createSaveHandler(id)}
                  onDelete={createDeleteHandler(id)}
                  readOnly={readOnly}
                  hasErrors={phraseValidationErrors[id]}
                />
              ))}
            </>
          )}

          {/* Empty State Banner */}
          {shouldShowEmptyStateBanner && (
            <Alert variant="info" className="p-2">
              <InfoIcon size={16} />
              <AlertDescription>{t('training_phrases.banner_text')}</AlertDescription>
            </Alert>
          )}
        </div>
      </Scrollbars>
    </>
  );
};

export default TrainingPhrases;
